import { add, addReferenceDataType, getPresentationProductType } from "csi/canvas/storeFactory";
import { registerBanner } from "csi/registry/bannerRegistry";
import { registerPageStore } from "csi/registry/pageStoreRegistry";
import { resolveNode } from "csi/service/nodeService";
import rootStore from "csi/state/rootStore";
import { C8Node, ICache } from "csi/typings";
import { useContext } from "react";
import PPBanner from "./PPBanner";
import {
	COLORMATERIAL,
	COLORWAY,
	COLOR_SPECIFICATION,
	FOOD,
	FOOD_MODULE,
	MATERIAL,
	PRESENTATION_RULE,
	PRODUCT_COLOR_SPECIFICATION,
	PRODUCT_PRESENTATION,
	PRODUCT_PRESENTATION_LAYOUT,
	PRODUCT_PRESENTATION_SET,
	PRODUCT_PRESENTATION_SET_ITEM,
	STYLE,
	THEME,
	THEME_MASTER,
} from "./PPService";
import PresentationPageStore from "./PresentationPageStore";
import { hasModule } from "csi/_base/modules";

function colorwayPathFunction(node, cache): string | null {
	const presentationType = getPresentationProductType(node, cache);
	if (presentationType === STYLE) {
		return "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Style)/Child:ProductColors(Colorway)";
	} else {
		return null;
	}
}

function colorMaterialPathFunction(node, cache): string | null {
	const presentationType = getPresentationProductType(node, cache);
	if (presentationType === MATERIAL) {
		return "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Material)/Child:ProductColors(ColorMaterial)";
	} else {
		return null;
	}
}

function stylePathFunction(node, cache): string | null {
	const presentationType = getPresentationProductType(node, cache);
	if (presentationType === STYLE) {
		return "Child:__Parent__/Child:PPProducts";
	} else {
		return null;
	}
}

function materialPathFunction(node, cache): string | undefined {
	const presentationType = getPresentationProductType(node, cache);
	if (presentationType === MATERIAL) {
		return "Child:__Parent__/Child:PPProducts";
	} else if (presentationType === STYLE) {
		return "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Product)/Child:Attributes(ProductAttributes)/Child:BOMMainMaterials(Material)";
	}
}

function foodPathFunction(node, cache): string | null {
	const presentationType = getPresentationProductType(node, cache);
	if (presentationType === FOOD) {
		return "Child:__Parent__/Child:PPProducts";
	} else {
		return null;
	}
}

add(PRODUCT_PRESENTATION_LAYOUT, PRODUCT_PRESENTATION, "Child:__Parent__");
add(PRODUCT_PRESENTATION_LAYOUT, STYLE, "Child:__Parent__/Child:PPProducts", stylePathFunction);
add(PRODUCT_PRESENTATION_LAYOUT, MATERIAL, "Child:__Parent__/Child:PPProducts", materialPathFunction);
if (hasModule(FOOD_MODULE)) {
	add(PRODUCT_PRESENTATION_LAYOUT, FOOD, "Child:__Parent__/Child:PPProducts", foodPathFunction);
}
add(PRODUCT_PRESENTATION_LAYOUT, PRODUCT_PRESENTATION_SET, "Child:__Parent__/Child:Sets");
add(PRODUCT_PRESENTATION_LAYOUT, PRODUCT_COLOR_SPECIFICATION, "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Product)/Child:ProductColors(ProductAttributes)/Child:ColorSpecification(ProductColorSpec)");
add(PRODUCT_PRESENTATION_LAYOUT, COLORWAY, "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Style)/Child:ProductColors(Colorway)", colorwayPathFunction);
add(PRODUCT_PRESENTATION_LAYOUT, COLORMATERIAL, "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Material)/Child:ProductColors(ColorMaterial)", colorMaterialPathFunction);
add(PRODUCT_PRESENTATION_LAYOUT, PRODUCT_PRESENTATION_SET_ITEM, "Child:__Parent__/Child:Sets/Child:Items");
add(PRODUCT_PRESENTATION_LAYOUT, THEME, "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Style)/Child:Theme(Theme)");
add(PRODUCT_PRESENTATION_LAYOUT, THEME_MASTER, "(ProductPresentationLayout)Child:__Parent__(ProductPresentation)/Child:PPProducts(Style)/Parent:Theme(Theme)/Child:__Parent__(ThemeMaster)");

// Reference Data for Root
function productPresentationLayoutStyleFunction(node, cache): boolean {
	return getPresentationProductType(node, cache) === STYLE;
}

function productPresentationLayoutMaterialFunction(node, cache): boolean {
	const presentationProductType = getPresentationProductType(node, cache);
	return presentationProductType === STYLE || presentationProductType === MATERIAL;
}

addReferenceDataType(PRODUCT_PRESENTATION_LAYOUT, PRODUCT_PRESENTATION);
addReferenceDataType(PRODUCT_PRESENTATION_LAYOUT, STYLE, productPresentationLayoutStyleFunction);
addReferenceDataType(PRODUCT_PRESENTATION_LAYOUT, MATERIAL, productPresentationLayoutMaterialFunction);
addReferenceDataType(PRODUCT_PRESENTATION_LAYOUT, COLOR_SPECIFICATION, productPresentationLayoutMaterialFunction);

// Reference Data for Selected Node
function productPresentationStylePathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType === STYLE) {
		return "Child:PPProducts";
	}
}

function productPresentationMaterialPathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType === MATERIAL) {
		return "Child:PPProducts";
	} else if (presentationProductType === STYLE) {
		return "(ProductPresentation)Child:PPProducts(Product)/Child:Attributes(ProductAttributes)/Child:BOMMainMaterials(Material)";
	}
}

function productPresentationColorSpecPathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType !== FOOD) {
		return "Child:PPProducts/Child:ProductColors/Child:ColorSpecification";
	}
}

function productPresentationFoodPathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType === FOOD) {
		return "Child:__Parent__/Child:PPProducts";
	}
}

function productPresentationSetColorSpecPathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType !== FOOD) {
		return "Child:__Parent__/Child:PPProducts/Child:ProductColors/Child:ColorSpecification";
	}
}

function productPresentationSetMaterialPathFunction(node, cache): string | undefined {
	const presentationProductType = getPresentationProductType(node, cache);
	if (presentationProductType === STYLE) {
		return "Child:Items/Child:PPIProduct/Child:Attributes/Child:BOMMainMaterials";
	}
}

add(STYLE, PRODUCT_PRESENTATION_SET, "Child:PPSetItems/Child:__Parent__");
add(STYLE, COLORWAY, "Child:ProductColors");
add(STYLE, COLOR_SPECIFICATION, "Child:ProductColors/Child:ColorSpecification");
add(STYLE, MATERIAL, "Child:Attributes/Child:BOMMainMaterials");

add(MATERIAL, PRODUCT_PRESENTATION_SET, "Child:PPSetItems/Child:__Parent__");
add(MATERIAL, COLORMATERIAL, "Child:ProductColors");
add(MATERIAL, COLOR_SPECIFICATION, "Child:ProductColors/Child:ColorSpecification");
add(MATERIAL, STYLE, "(Material)Parent:BOMMainMaterials(ApparelBOMRevision)/Child:__Parent__(Style)");

add(FOOD, PRODUCT_PRESENTATION_SET, "Child:PPSetItems/Child:__Parent__");

add(PRODUCT_PRESENTATION, PRODUCT_PRESENTATION_SET, "Child:Sets");
add(PRODUCT_PRESENTATION, STYLE, undefined, productPresentationStylePathFunction);
add(PRODUCT_PRESENTATION, MATERIAL, undefined, productPresentationMaterialPathFunction);
if (hasModule(FOOD_MODULE)) {
	add(PRODUCT_PRESENTATION, FOOD, undefined, productPresentationFoodPathFunction);
}
add(PRODUCT_PRESENTATION, COLOR_SPECIFICATION, undefined, productPresentationColorSpecPathFunction);

add(PRODUCT_PRESENTATION_SET, PRODUCT_PRESENTATION_SET_ITEM, "Child:Items");
add(PRODUCT_PRESENTATION_SET, COLOR_SPECIFICATION, undefined, productPresentationSetColorSpecPathFunction);
add(PRODUCT_PRESENTATION_SET, MATERIAL, undefined, productPresentationSetMaterialPathFunction);

add(COLOR_SPECIFICATION, COLORMATERIAL, "(ColorSpecification)Parent:ColorSpecification(ColorMaterial)");
add(COLOR_SPECIFICATION, COLORWAY, "(ColorSpecification)Parent:ColorSpecification(Colorway)");

add(COLORMATERIAL, MATERIAL, "(ColorMaterial)Child:__Material__(Material)");
add(COLORMATERIAL, COLOR_SPECIFICATION, "(ColorMaterial)Child:ColorSpecification(ColorSpecification)");
add(COLORMATERIAL, PRODUCT_PRESENTATION_SET, "Child:PPSetItems/Child:__Parent__");

add(COLORWAY, STYLE, "Child:__Parent__");
add(COLORWAY, COLOR_SPECIFICATION, "Child:ColorSpecification");
add(COLORWAY, PRODUCT_PRESENTATION_SET, "Child:PPSetItems/Child:__Parent__");

// register the banner for PP node
registerBanner(PRODUCT_PRESENTATION, PPBanner);

/** initialize the page store and populate the context  */
const _initializer = async (node: C8Node, revisionNode: C8Node, cache: ICache): Promise<PresentationPageStore> => {
	let finalNode = node;

	if (node.$Type === PRODUCT_PRESENTATION_SET) {
		finalNode = await resolveNode(node.__Parent__, cache);
	}

	const runtimeRuleNode = await resolveNode(finalNode[PRESENTATION_RULE]);
	const store = new PresentationPageStore(finalNode, cache, runtimeRuleNode);

	return store;
};

registerPageStore(PRODUCT_PRESENTATION, _initializer);
registerPageStore(PRODUCT_PRESENTATION_SET, _initializer);

/** hook to use page store */
export function usePresentationPageStore(): PresentationPageStore {
	return useContext(rootStore.pageContext) as PresentationPageStore;
}